import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { VideoThumbnailGenerator } from 'browser-video-thumbnail-generator';
import { apiHandler } from '../../../api/apihandler';
import { handleImageUpload } from '../../../api/uploads';
import { INFLUENCER_POSTED_CHALLENGES, FEED_POST, FEED_MEDIA_POST } from '../../../api/constants';
import { apiSucceeded, apiErrorMessage, getAvatarColor, getInitials } from '../../../views/dashboard/feed/feedUtils';
import MediaPlaceholderIcon from '../../../components/nemonto/MediaPlaceholderIcon';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import EmptyState from '../../../components/nemonto/EmptyState';
import '../../../views/dashboard/social/social-list-page.css';
import './home-app-page.css';
import './challenge-detail-page.css';
import './shape-challenges-page.css';

const PAGE_LIMIT = 10;

const formatTimeLeft = (dateStr) => {
  if (!dateStr) return null;
  const diffMs = new Date(dateStr).getTime() - Date.now();
  if (diffMs <= 0) return null;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${Math.max(diffMin, 1)}m left`;
  if (diffMin < 2880) return `${Math.floor(diffMin / 60)}h left`;
  return `${Math.floor(diffMin / 1440)}d left`;
};

const Avatar = ({ url, name, className }) => {
  if (url) return <img src={url} alt={name} className={className} />;
  return (
    <div className={className} style={{ background: getAvatarColor(name || 'User'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

// Status pill shown on each tile - depends on the item's own bucket
// (the challenge_type the card was fetched under: "live"/"ending_soon"/
// "ended", or "" for the "All" tab) rather than the selected tab alone,
// since a server-filtered bucket is authoritative and shouldn't be
// re-derived from expire_at and get it wrong. The "All" tab's bucket is ""
// (no server-side status filter applied), so those cards fall through to
// the same client-side expire_at/winner heuristic used before this filter
// existed.
const ChallengeCardTile = ({ item, onOpen }) => {
  const isWinnerDeclared = item.winnerParticipateId > 0;
  const isExpired = !!item.expireAt && new Date(item.expireAt).getTime() < Date.now();
  const timeLeft = formatTimeLeft(item.expireAt);

  let statusClass;
  let statusLabel;
  if (item.bucket === 'ended' || isWinnerDeclared) {
    statusClass = isWinnerDeclared ? 'winner' : 'ended';
    statusLabel = isWinnerDeclared ? 'Winner declared' : 'Challenge ended';
  } else if (item.bucket === 'ending_soon') {
    statusClass = 'ending';
    statusLabel = timeLeft ? `Ending · ${timeLeft}` : 'Ending soon';
  } else if (isExpired) {
    statusClass = 'ended';
    statusLabel = 'Challenge ended';
  } else {
    const isEnding = !!timeLeft && !timeLeft.startsWith('1d') && timeLeft.includes('h left');
    statusClass = isEnding ? 'ending' : 'live';
    statusLabel = timeLeft ? `${isEnding ? 'Ending' : 'Live'} · ${timeLeft}` : 'Live now';
  }

  const isEnded = statusClass === 'ended';

  return (
    <div className={`ch-card${isEnded ? ' ch-card-ended' : ''}`} onClick={() => onOpen(item)} role="button" tabIndex={0}>
      <div className="ch-thumb">
        {item.image ? <img src={item.image} alt="" /> : <MediaPlaceholderIcon size={60} />}
        <span className={`ch-status-pill ${statusClass}`}>
          {statusClass === 'live' && <span className="ch-dot" />}
          {statusLabel}
        </span>
      </div>
      <div className="ch-body">
        <div className="ch-host-row">
          <Avatar url={item.hostPhoto} name={item.hostName} className="ch-host-avatar" />
          <span className="ch-host-name">{item.hostName}</span>
        </div>
        <p className="ch-title">{item.title}</p>
        <div className="ch-foot">
          <span className="ch-entries"><b>{item.entries}</b> entries</span>
          <span className="ch-enter-btn">{isEnded ? 'View details' : 'View challenge'}</span>
        </div>
      </div>
    </div>
  );
};

// Nemonto-themed "post a challenge" form - same shape as
// challenge-detail-page.js's ChallengeParticipateScreen (photo/video grid +
// note) but posts a new type=2/3 challenge via FEED_POST/FEED_MEDIA_POST
// (the same two-phase create/upload/finalize flow create-recipe-page.js
// uses) instead of participating in an existing one. Replaces the old
// Bootstrap CreatePostModal, whose postType="2"/"3" handling was fully
// built but never actually wired to any caller.
const ChallengeCreateScreen = ({ type, onClose }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => () => {
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    videos.forEach((vid) => URL.revokeObjectURL(vid.preview));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAddImages(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setImages((prev) => [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  }

  async function handleAddVideos(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      let thumbFile = null;
      let thumbPreview = null;
      try {
        const generator = new VideoThumbnailGenerator(preview);
        const thumbnail = await generator.getThumbnail();
        thumbPreview = thumbnail?.thumbnail;
        if (thumbPreview) {
          const blob = await (await fetch(thumbPreview)).blob();
          thumbFile = new File([blob], `${+new Date()}.jpg`, { type: 'image/jpeg' });
        }
      } catch (err) {
        console.error(err);
      }
      setVideos((prev) => [...prev, { file, preview, thumbFile, thumbPreview }]);
    }
  }

  function removeImage(idx) {
    setImages((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  function removeVideo(idx) {
    setVideos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  async function submit() {
    if (!title.trim()) {
      Swal.fire('Required', 'Please give your challenge a title.', 'warning');
      return;
    }
    if (!content.trim()) {
      Swal.fire('Required', 'Please add a description.', 'warning');
      return;
    }
    if (!expireDate) {
      Swal.fire('Required', 'Please select an entries-close date.', 'warning');
      return;
    }

    setIsLoading(true);
    setUploadStatus('Posting…');
    try {
      const basePayload = {
        title: title.trim(),
        content: content.trim(),
        type,
        is_completed: 0,
        expire_at: expireDate,
        event_id: '',
        feed_user_tags: [],
      };
      const res = await apiHandler(FEED_POST, 'POST', basePayload);
      if (!apiSucceeded(res)) {
        throw new Error(apiErrorMessage(res, 'Failed to post your challenge.'));
      }
      const postId = res?.data?.data?.feed?.id;

      for (const img of images) {
        setUploadStatus('Uploading photo…');
        const url = await handleImageUpload(img.file, 'feedImages/', 'image/jpeg');
        await apiHandler(FEED_MEDIA_POST, 'POST', { post_id: postId, path: url, media_type: '0', thumb: '' });
      }
      for (const vid of videos) {
        setUploadStatus('Uploading video…');
        const url = await handleImageUpload(vid.file, 'feedVideos/', 'video/*');
        const thumbUrl = vid.thumbFile ? await handleImageUpload(vid.thumbFile, 'feedVideosThumb/', 'image/jpeg') : '';
        await apiHandler(FEED_MEDIA_POST, 'POST', { post_id: postId, path: url, media_type: '1', thumb: thumbUrl });
      }

      setUploadStatus('Finishing up…');
      const finalRes = await apiHandler(FEED_POST, 'POST', { ...basePayload, post_id: postId, is_completed: 1 });
      if (!apiSucceeded(finalRes)) {
        throw new Error(apiErrorMessage(finalRes, 'Failed to finish posting your challenge.'));
      }

      Swal.fire('Success', 'Your challenge has been posted!', 'success').then(() => onClose(true));
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setIsLoading(false);
      setUploadStatus('');
    }
  }

  return (
    <div>
      <div className="nmha-entry-header">
        <button type="button" onClick={() => onClose(false)} disabled={isLoading} style={{ fontSize: 20 }}>✕</button>
        <h2 className="nmha-entry-title">New challenge</h2>
        <div style={{ width: 24 }} />
      </div>

      <div className="nmha-entry-wrap">
        <p className="nmha-entry-label">Challenge title</p>
        <input
          type="text"
          className="nmha-entry-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Shorshe Ilish Challenge"
          disabled={isLoading}
        />

        <p className="nmha-entry-label">Description</p>
        <textarea
          className="nmha-entry-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Tell cooks what to make and how to enter…"
          disabled={isLoading}
        />

        <p className="nmha-entry-label">Entries close on</p>
        <input
          type="date"
          className="nmha-entry-input"
          min={new Date().toISOString().split('T')[0]}
          value={expireDate}
          onChange={(e) => setExpireDate(e.target.value)}
          disabled={isLoading}
        />

        <p className="nmha-entry-label">Cover photo/video (optional)</p>
        <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddImages} />
        <input ref={videoInputRef} type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={handleAddVideos} />
        <div className="nmch-participate-media-grid">
          {images.map((img, i) => (
            <div key={`img-${i}`} className="nmch-participate-media-tile">
              <img src={img.preview} alt="" />
              <button type="button" className="nmch-participate-media-remove" onClick={() => removeImage(i)} disabled={isLoading}>✕</button>
            </div>
          ))}
          {videos.map((vid, i) => (
            <div key={`vid-${i}`} className="nmch-participate-media-tile">
              {vid.thumbPreview ? <img src={vid.thumbPreview} alt="" /> : <video src={vid.preview} muted />}
              <button type="button" className="nmch-participate-media-remove" onClick={() => removeVideo(i)} disabled={isLoading}>✕</button>
            </div>
          ))}
          <div className="nmch-participate-add-tile" onClick={() => !isLoading && imageInputRef.current?.click()}>
            <span className="material-symbols-outlined">photo_camera</span>
            Photo
          </div>
          <div className="nmch-participate-add-tile" onClick={() => !isLoading && videoInputRef.current?.click()}>
            <span className="material-symbols-outlined">videocam</span>
            Video
          </div>
        </div>

        <div className="nmha-entry-footer">
          <button type="button" className="nmha-entry-post-btn" onClick={submit} disabled={isLoading}>Post challenge</button>
        </div>
      </div>

      {isLoading && (
        <div className="nmha-overlay">
          <div className="nmha-overlay-card">
            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{uploadStatus || 'Posting…'}</p>
            <p style={{ fontSize: 12, color: '#9a8c84', margin: 0 }}>Don&apos;t close this screen</p>
          </div>
        </div>
      )}
    </div>
  );
};

// challenge_type values the backend accepts on INFLUENCER_POSTED_CHALLENGES:
// "live" | "ending_soon" | "ended" | "" (empty string = all challenges,
// no status filter applied).
const CHALLENGE_TABS = [
  { key: '', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'ending_soon', label: 'Ending soon' },
  { key: 'ended', label: 'Ended' },
];

const CHALLENGE_EMPTY_COPY = {
  '': { title: 'No challenges yet', sub: 'Check back soon for new challenges to join.' },
  live: { title: 'No live challenges', sub: 'Live challenges will show up here once one kicks off.' },
  ending_soon: { title: 'Nothing ending soon', sub: 'Challenges close to their deadline will show up here.' },
  ended: { title: 'No ended challenges', sub: 'Completed challenges will show up here once they end.' },
};

// Live, nemonto-themed challenges listing - replaces the legacy Bootstrap
// dancecorner-challenges.js/influencer-challenges.js pages, reusing the
// visual design built for the (previously static-mockup-only) "Recipe
// Challenges" page. Both "Nemonto Challenges" (dance corner, type=2) and
// "Shape Challenges" (influencer, type=3) sidebar entries render this same
// component, distinguished only by the `type` param sent to the shared
// INFLUENCER_POSTED_CHALLENGES endpoint - same split the legacy pages used.
const ShapeChallengesPage = ({ type, title, subtitle }) => {
  const navigate = useNavigate();
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;
  // Only the role this specific challenge type belongs to may host one here
  // - a dance-corner user can't post a Shape Challenge and vice versa.
  const canPostChallenge = type === '2'
    ? profileData?.user_setting?.is_dance_corner === 1
    : type === '3'
      ? profileData?.user_setting?.is_influencer === 1
      : false;
  const [showCreateFlow, setShowCreateFlow] = useState(false);
  const [challengeType, setChallengeType] = useState('');
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Guards against out-of-order responses: switching tabs quickly fires a
  // new loadPage before the previous tab's request has resolved, and
  // without this the older (now-stale) response can land last and
  // overwrite the newer tab's data - since only the most recently issued
  // request's requestId still matches the ref when it resolves, any
  // earlier in-flight response is silently discarded instead.
  const requestIdRef = useRef(0);

  async function loadPage(pageNo, replace, activeChallengeType) {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await apiHandler(INFLUENCER_POSTED_CHALLENGES, 'POST', {
        page_no: pageNo,
        limit: PAGE_LIMIT,
        type,
        challenge_type: activeChallengeType,
        address: '',
        userSlug: '',
        hashtag: '',
        user_id: '',
      });
      if (requestId !== requestIdRef.current) return;
      if (!apiSucceeded(res)) {
        if (replace) setChallenges([]);
        setHasMore(false);
        return;
      }

      const raw = res?.data?.data?.userPosts?.data || [];
      const meta = res?.data?.data?.userPosts?.meta;
      // Trust the bucket this list was requested under for display, rather
      // than each item's own challenge_status: the two are different
      // vocabularies (challenge_type is "live"/"ending_soon"/"ended"/"",
      // challenge_status is "live"/"upcoming"/"past") and only the former
      // is meaningful for a status-filtered list. challenge_status is still
      // the right signal on the single-item detail fetch, which has no
      // list-level bucket.
      const mapped = raw.map((d) => {
        const firstMedia = d.user_post_images?.[0] || null;
        return {
          id: d.id,
          bucket: activeChallengeType,
          title: d.title || d.name || d.content || 'Challenge',
          image: firstMedia ? (firstMedia.media_type === 1 ? firstMedia.thumb : firstMedia.image_path) : null,
          hostName: `${d.user?.first_name || ''} ${d.user?.last_name || ''}`.trim() || 'User',
          hostPhoto: d.user?.user_setting?.photo || null,
          entries: d.participates_count || 0,
          expireAt: d.expire_at || null,
          winnerParticipateId: d.winner_participate_id ?? 0,
        };
      });
      setChallenges((prev) => (replace ? mapped : [...prev, ...mapped]));
      setHasMore(!!meta && meta.current_page < meta.last_page);
      setPage(pageNo);
    } catch (e) {
      if (requestId === requestIdRef.current) console.error(e);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    loadPage(1, true, challengeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, challengeType]);

  function selectTab(tab) {
    if (tab === challengeType) return;
    setChallenges([]);
    setChallengeType(tab);
  }

  function openChallenge(item) {
    // The detail page has no server-side bucket of its own (it fetches a
    // single post by id) - pass along a status hint so it can show the
    // right pill before its own fetch resolves, instead of re-deriving it
    // from expire_at alone and getting it wrong. challenge-detail-page.js's
    // hint uses the challenge_status vocabulary ("live"/"upcoming"/"past"),
    // which is different from this list's challenge_type filter
    // ("live"/"ending_soon"/"ended"/"") - translate between the two here.
    const bucketHint = item.bucket === 'ended'
      ? 'past'
      : (item.bucket === 'live' || item.bucket === 'ending_soon') ? 'live' : undefined;
    navigate(`/pages/challenge/${item.id}`, { state: { bucket: bucketHint } });
  }

  function handleCreateClose(didPost) {
    setShowCreateFlow(false);
    if (didPost) {
      setChallenges([]);
      loadPage(1, true, challengeType);
    }
  }

  const emptyCopy = CHALLENGE_EMPTY_COPY[challengeType];

  return (
    <div className="nmch-root">
      <div className="nmch-app">
        <main className="nmch-feed-col">
          {showCreateFlow ? (
            <ChallengeCreateScreen type={type} onClose={handleCreateClose} />
          ) : (
            <>
              <div className="nmch-feed-header">
                <div className="nmch-feed-header-top">
                  <div>
                    <h1>{title}</h1>
                    <p className="nmch-feed-sub">{subtitle}</p>
                  </div>
                  {canPostChallenge && (
                    <button type="button" className="nmch-new-btn" onClick={() => setShowCreateFlow(true)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span> New challenge
                    </button>
                  )}
                </div>
                <div className="ch-tabs">
                  {CHALLENGE_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`ch-tab${challengeType === tab.key ? ' active' : ''}`}
                      onClick={() => selectTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="nmch-feed-body">
                {loading && challenges.length === 0 ? (
                  <p className="nmch-loading-text">Loading challenges…</p>
                ) : challenges.length === 0 ? (
                  <EmptyState icon="emoji_events" title={emptyCopy.title} sub={emptyCopy.sub} />
                ) : (
                  <div className="ch-grid">
                    {challenges.map((item) => (
                      <ChallengeCardTile key={item.id} item={item} onOpen={openChallenge} />
                    ))}
                  </div>
                )}

                {hasMore && (
                  <div className="nmch-load-more-wrap">
                    <button type="button" className="nmch-load-more-btn" onClick={() => loadPage(page + 1, false, challengeType)} disabled={loading}>
                      {loading ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        <aside className="nmch-right-rail">
          <PeopleYouMayKnowWidget />
          <MyStatsWidget />
        </aside>
      </div>
    </div>
  );
};

export default ShapeChallengesPage;
